/** Per-thread composer draft persistence (survives tab reloads and discards). */

export const COMPOSER_DRAFT_STORAGE_PREFIX = "rakazo.composerDraft.";

type ComposerDraftSubmission = { text: string };
const pendingSubmissions = new Map<string, ComposerDraftSubmission>();

/** How long typing must pause before the draft is written to storage. */
export const COMPOSER_DRAFT_WRITE_DELAY_MS = 250;
/** Text typed but not yet written, per thread. Flushed on blur, unmount and page hide. */
const waitingDrafts = new Map<string, string>();
let writeTimer: ReturnType<typeof setTimeout> | undefined;
const acceptedListeners = new Map<string, Set<(text: string) => void>>();
const draftRevisions = new Map<string, number>();

export function composerDraftRevision(threadKey: string): number {
  return draftRevisions.get(threadKey) ?? 0;
}

/** Any human composition edit detaches an older submission. / Будь-яке редагування композиції відокремлює попереднє надсилання. */
export function invalidateComposerDraft(threadKey: string): void {
  draftRevisions.set(threadKey, composerDraftRevision(threadKey) + 1);
  pendingSubmissions.delete(threadKey);
}

export function beginComposerDraftSubmission(
  threadKey: string,
  text: string,
): ComposerDraftSubmission {
  writeComposerDraft(threadKey, text);
  const submission = { text };
  pendingSubmissions.set(threadKey, submission);
  return submission;
}

export function settleComposerDraftSubmission(
  threadKey: string,
  submission: ComposerDraftSubmission,
  accepted: boolean,
): void {
  if (pendingSubmissions.get(threadKey) !== submission) return;
  pendingSubmissions.delete(threadKey);
  if (!accepted || readComposerDraft(threadKey) !== submission.text) return;
  writeComposerDraft(threadKey, "");
  for (const listener of acceptedListeners.get(threadKey) ?? []) listener(submission.text);
}

let unsavedWork = false;

/**
 * The shell reports work a reload would lose: a send (typed or spoken) until the server answers,
 * or picked attachments, which live only in memory.
 */
export function setComposerUnsavedWork(pending: boolean): void {
  unsavedWork = pending;
}

/** A send waiting for its answer, or attachments that only exist in memory. */
export function composerHoldsUnsavedWork(): boolean {
  return pendingSubmissions.size > 0 || unsavedWork;
}

/** Unsaved composer work, or saved text in any composer that has not been sent. */
export function hasUnsentComposerDraft(storage: Storage | null = null): boolean {
  if (composerHoldsUnsavedWork()) return true;
  // Text still waiting for its debounced write is unsent too: write it before scanning.
  if (storage === null) flushComposerDrafts();
  try {
    const drafts = storage ?? localStorage;
    for (let index = 0; index < drafts.length; index++) {
      const key = drafts.key(index);
      if (key?.startsWith(COMPOSER_DRAFT_STORAGE_PREFIX) && drafts.getItem(key)) return true;
    }
  } catch {
    // Without storage the draft lives only in memory, so treat it as unsent.
    return true;
  }
  return false;
}

export function subscribeComposerDraftAccepted(
  threadKey: string,
  listener: (text: string) => void,
): () => void {
  let listeners = acceptedListeners.get(threadKey);
  if (!listeners) {
    listeners = new Set();
    acceptedListeners.set(threadKey, listeners);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) acceptedListeners.delete(threadKey);
  };
}

/** A retry clears only its original draft. / Повтор очищає лише власну початкову чернетку. */
export function clearAcceptedComposerDraft(
  threadKey: string,
  text: string,
  revision: number,
): void {
  if (composerDraftRevision(threadKey) !== revision) return;
  flushComposerDrafts();
  if (readComposerDraft(threadKey) !== text) return;
  writeComposerDraft(threadKey, "");
  for (const listener of acceptedListeners.get(threadKey) ?? []) listener(text);
}

type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function getLocalStorage(): DraftStorage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function composerDraftStorageKey(threadKey: string): string {
  return `${COMPOSER_DRAFT_STORAGE_PREFIX}${threadKey}`;
}

export function readComposerDraft(
  threadKey: string,
  storage: DraftStorage | null = getLocalStorage(),
): string {
  if (!storage || !threadKey) return "";
  try {
    return storage.getItem(composerDraftStorageKey(threadKey)) ?? "";
  } catch {
    return "";
  }
}

function persistComposerDraft(threadKey: string, draft: string, storage: DraftStorage | null) {
  if (!storage || !threadKey) return;
  try {
    if (draft.length === 0) storage.removeItem(composerDraftStorageKey(threadKey));
    else storage.setItem(composerDraftStorageKey(threadKey), draft);
  } catch {
    // Quota or private-mode failures: the draft still lives in memory.
  }
}

/** Writes now. Supersedes any text still waiting for this thread. */
export function writeComposerDraft(
  threadKey: string,
  draft: string,
  storage: DraftStorage | null = getLocalStorage(),
): void {
  invalidateComposerDraft(threadKey);
  waitingDrafts.delete(threadKey);
  persistComposerDraft(threadKey, draft, storage);
}

/**
 * The keystroke path: keeps the text in memory and writes it once typing pauses, so a large
 * draft is not serialised into storage on every character. Like any edit it detaches a send
 * that is still waiting for its answer.
 * Шлях набору: пише в сховище, коли набір затихає, а не на кожен символ.
 */
export function scheduleComposerDraftWrite(threadKey: string, draft: string): void {
  invalidateComposerDraft(threadKey);
  waitingDrafts.set(threadKey, draft);
  clearTimeout(writeTimer);
  writeTimer = setTimeout(flushComposerDrafts, COMPOSER_DRAFT_WRITE_DELAY_MS);
}

/** Writes all waiting text now: on blur, send, unmount and page hide. */
export function flushComposerDrafts(): void {
  clearTimeout(writeTimer);
  writeTimer = undefined;
  if (waitingDrafts.size === 0) return;
  const storage = getLocalStorage();
  const waiting = [...waitingDrafts];
  waitingDrafts.clear();
  for (const [threadKey, draft] of waiting) persistComposerDraft(threadKey, draft, storage);
}
